import { useEffect, useState } from "react";
import { MENU } from "../data/cafe";

export default function MenuPage() {
  const [active, setActive] = useState(MENU[0].category);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" },
    );
    MENU.forEach((c) => {
      const el = document.getElementById(c.category);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="page-menu" className="pt-32 pb-20 overflow-x-hidden">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
        <span className="text-xs uppercase tracking-[0.4em] text-[#FF8C5A]">
          The Menu
        </span>
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl mt-4 leading-[1.05]">
          Every order, a small <span className="italic text-gradient">ritual.</span>
        </h1>
        <p className="text-white/60 mt-6 max-w-2xl text-base sm:text-lg">
          Fourteen categories. Over 150 hand-crafted creations. Prices in INR
          (₹). All items prepared fresh; please allow a moment.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 mt-10 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Sidebar - horizontal pills on mobile, sticky list on desktop */}
        <aside className="lg:col-span-3 lg:sticky lg:top-28 lg:self-start min-w-0">
          {/* Mobile: sticky horizontal scrolling category pills */}
          <div className="lg:hidden sticky top-20 z-30 -mx-5 px-5 py-3 bg-[#050505]/90 backdrop-blur border-b border-white/10">
            <nav
              data-testid="menu-mobile-nav"
              className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1"
            >
              {MENU.map((c) => (
                <button
                  key={c.category}
                  data-testid={`menu-nav-mobile-${c.category.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => scrollTo(c.category)}
                  className={`whitespace-nowrap shrink-0 px-4 py-2 rounded-full text-xs sm:text-sm transition border ${
                    active === c.category
                      ? "border-[#FF8C5A] text-black bg-warm-gradient"
                      : "border-white/15 text-white/70 hover:text-white"
                  }`}
                >
                  {c.category}
                </button>
              ))}
            </nav>
          </div>

          {/* Desktop: full sticky nav list */}
          <div className="hidden lg:block">
            <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
              Sections
            </div>
            <nav className="flex flex-col gap-2">
              {MENU.map((c) => (
                <button
                  key={c.category}
                  data-testid={`menu-nav-${c.category.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => scrollTo(c.category)}
                  className={`text-left px-3 py-2 rounded-lg text-sm transition border ${
                    active === c.category
                      ? "border-[#FF8C5A]/40 text-white bg-[#FF8C5A]/10"
                      : "border-transparent text-white/55 hover:text-white"
                  }`}
                >
                  {c.category}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <div className="lg:col-span-9 space-y-20 md:space-y-24 min-w-0">
          {MENU.map((cat) => (
            <section
              key={cat.category}
              id={cat.category}
              data-testid={`menu-section-${cat.category.toLowerCase().replace(/\s+/g, "-")}`}
              className="scroll-mt-40 lg:scroll-mt-28"
            >
              <div className="flex items-end justify-between gap-3 border-b border-white/10 pb-5 md:pb-6 mb-6 md:mb-8">
                <div className="min-w-0 flex-1">
                  <h2 className="font-display text-2xl sm:text-3xl md:text-5xl break-words">
                    {cat.category}
                  </h2>
                  <p className="text-white/50 mt-2 italic text-sm sm:text-base">{cat.blurb}</p>
                </div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/30 whitespace-nowrap shrink-0">
                  {cat.items.length} items
                </span>
              </div>
              <ul className="divide-y divide-white/5">
                {cat.items.map((item) => (
                  <li
                    key={item.name}
                    className="py-3.5 md:py-4 group"
                  >
                    <div className="flex items-baseline gap-3 md:gap-4">
                      <span className="font-display text-[15px] sm:text-lg md:text-xl text-white group-hover:text-[#FF8C5A] transition flex-1 min-w-0 break-words leading-snug">
                        {item.name}
                      </span>
                      <span className="hidden sm:block flex-1 mx-2 border-b border-dotted border-white/15 translate-y-[-4px]" />
                      <span className="text-white/80 tabular-nums whitespace-nowrap shrink-0 text-sm sm:text-base">
                        ₹{item.price}
                      </span>
                    </div>
                    {item.desc && (
                      <div className="text-xs sm:text-sm text-white/40 mt-1.5 pr-14">
                        {item.desc}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
